import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { findPostBySlug, BLOG_POSTS } from "../data/blogPosts";
import { SectionEyebrow } from "../components/SectionEyebrow";

const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("es-CO", { day: "2-digit", month: "long", year: "numeric" });
};

export default function BlogPost() {
    const { slug } = useParams();
    const post = findPostBySlug(slug);
    if (!post) return <Navigate to="/blog" replace />;

    const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

    return (
        <article data-testid={`blog-post-${post.slug}`}>
            <section className="topo-bg py-16 lg:py-24">
                <div className="container-fe max-w-4xl">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:gap-3 transition-all" data-testid="blog-post-back">
                        <ArrowLeft size={16} /> Volver al blog
                    </Link>
                    <div className="mt-8">
                        <SectionEyebrow>{post.category}</SectionEyebrow>
                        <h1 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-tight">
                            {post.title}
                        </h1>
                        <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
                            <span className="flex items-center gap-2"><User size={14} className="text-brand-green" /> {post.author}</span>
                            <span className="flex items-center gap-2"><Calendar size={14} className="text-brand-green" /> {formatDate(post.date)}</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-white">
                <div className="container-fe max-w-4xl -mt-8 relative">
                    <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                        <img
                            src={post.image}
                            alt={post.title}
                            style={post.imagePosition ? { objectPosition: post.imagePosition } : undefined}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
                <div className="container-fe max-w-3xl py-16 lg:py-20">
                    <div className="prose prose-lg max-w-none">
                        {post.content.split("\n\n").map((para) => (
                            <p key={para.slice(0, 40)} className="text-lg text-muted-foreground leading-relaxed mb-6">{para}</p>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-20 bg-brand-cream">
                <div className="container-fe">
                    <SectionEyebrow>También te puede interesar</SectionEyebrow>
                    <h2 className="mt-4 font-display font-bold text-2xl lg:text-4xl text-brand-navy leading-tight">
                        Más historias del territorio
                    </h2>
                    <div className="mt-10 grid md:grid-cols-3 gap-6">
                        {related.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                data-testid={`blog-related-${p.slug}`}
                                className="group rounded-2xl overflow-hidden border border-black/5 bg-white hover:border-brand-green/30 hover:-translate-y-1 transition-all"
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={p.image}
                                        alt={p.title}
                                        style={p.imagePosition ? { objectPosition: p.imagePosition } : undefined}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="text-xs uppercase tracking-wider font-bold text-brand-green">{p.category}</div>
                                    <h3 className="mt-2 font-display font-semibold text-brand-navy leading-snug">{p.title}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </article>
    );
}
